import { Link } from 'react-router-dom';

import { useToastStore, type ToastVariant } from './toast';

/**
 * Renders active toasts, top-right, stacked. Mounted once in the app shell (Layout).
 * Semantic colours (§8.3) carry the meaning; text stays dark for readability.
 */
export function Toaster(): JSX.Element {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);

  return (
    <div
      className="pointer-events-none fixed right-4 top-20 z-[100] flex w-full max-w-sm flex-col gap-2 px-4 sm:px-0"
      aria-live="polite"
      role="region"
      aria-label="Notifications"
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          role={t.variant === 'error' ? 'alert' : 'status'}
          className={`toast-in pointer-events-auto flex items-start gap-3 rounded-lg border border-frost border-l-4 bg-white p-3 pr-2 shadow-hover ${ACCENT[t.variant]}`}
        >
          <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${BADGE[t.variant]}`}>
            {GLYPH[t.variant]}
          </span>
          <div className="flex-1 py-0.5">
            <p className="text-sm text-navy-900">{t.message}</p>
            {t.action && (
              <Link
                to={t.action.to}
                onClick={() => dismiss(t.id)}
                className="mt-1 inline-block text-sm font-medium text-sky-ink hover:underline"
              >
                {t.action.label} →
              </Link>
            )}
          </div>
          <button
            type="button"
            onClick={() => dismiss(t.id)}
            aria-label="Dismiss notification"
            className="tap-target shrink-0 rounded p-1 text-neutral hover:text-navy-900"
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}

const ACCENT: Record<ToastVariant, string> = {
  success: 'border-l-success',
  error: 'border-l-danger',
  info: 'border-l-info',
};

const BADGE: Record<ToastVariant, string> = {
  success: 'bg-success/12 text-green-ink',
  error: 'bg-danger/12 text-danger',
  info: 'bg-info/12 text-info',
};

const GLYPH: Record<ToastVariant, string> = {
  success: '✓',
  error: '✕',
  info: 'i',
};
